import React, { useRef, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import { Field, Form, Formik } from 'formik';
import { InputField } from '../../../../../components/formFields/InputField';
import Label from '@/components/Label';
import { apiService } from '../../../../../utils/api';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/RadioGroup';
import { SolutionPropertyDefinitionModalProps } from '../../types';

const SolutionPropertyDefinitionModal = ({
  visible,
  formProps,
  onSubmit,
  propertyId,
  onClose,
}: SolutionPropertyDefinitionModalProps) => {
  const formRef = useRef<any>(null);
  const [propertyData, setPropertyData] = useState<any>(null);

  useEffect(() => {
    if (!propertyId) return;

    const fetchPropertyDefinition = async () => {
      try {
        const result = await apiService.get(
          `/api/v1/property-definition/${propertyId}/last-version/`
        );
        setPropertyData(result.data);
      } catch (e) {
        console.log(e);
      }
    };

    fetchPropertyDefinition();
  }, [propertyId]);

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    formRef.current.submitForm();
    setPropertyData(null);
  };

  const hasPredefinedValues = propertyData?.value_choices && propertyData?.value_choices.length > 0;

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>{formProps?.property_definition?.title ?? 'Editace hodnoty'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
              value: formProps?.value,
              description: formProps?.description,
            }}
            onSubmit={(values) => {
              onSubmit(values);
            }}
          >
            {(props) => {
              const { values, setFieldValue } = props;
              return (
                <div className="pb-4">
                  <Form>
                    <div className="space-y-1">
                      <Label name="value" title="Hodnota" required />
                      {hasPredefinedValues ? (
                        <RadioGroup
                          value={values.value ? String(values.value) : ''}
                          onValueChange={(val) => setFieldValue('value', val)}
                        >
                          <table className="table-auto w-full">
                            <thead className="text-left bg-color4 text-color2 text-sm font-semibold">
                              <tr className="w-full table table-fixed">
                                <th className="p-2 w-8"></th>
                                <th className="p-2">Hodnota</th>
                                <th>Popisek</th>
                              </tr>
                            </thead>
                            <tbody className="block text-sm max-h-[300px] overflow-y-auto">
                              {(propertyData?.value_choices || []).map((item: any, i: number) => (
                                <tr key={i} className="border-b w-full table table-fixed">
                                  <td className="p-2 w-8">
                                    <RadioGroupItem
                                      value={String(item.value)}
                                      id={`value-choice-${i}`}
                                      data-testid={`solution-prop-def-modal-radio-${i}`}
                                    />
                                  </td>
                                  <td className="p-2">{item.value}</td>
                                  <td className="p-2">{item.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </RadioGroup>
                      ) : (
                        <Field
                          name="value"
                          value={values.value || null}
                          placeholder="Hodnota"
                          component={InputField}
                          data-testid="solution-prop-def-modal-value-input"
                        />
                      )}
                    </div>
                  </Form>
                </div>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            onClick={() => {
              setPropertyData(null);
              onClose();
            }}
            data-testid="solution-prop-def-modal-close-btn"
          >
            Zavřít
          </Button>
          <Button
            key={2}
            onClick={() => onSubmitButton()}
            data-testid="solution-prop-def-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SolutionPropertyDefinitionModal;
